import { beginLoading, endLoading } from '@app/AppLayout/LoadingContext';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { RemoveConfirmModal } from '@app/components/RemoveConfirmModal';
import {
  AlertVariant,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownList,
  MenuToggle,
  MenuToggleElement
} from '@patternfly/react-core';
import { EllipsisVIcon } from '@patternfly/react-icons';
import React from 'react';

/** A page specific option, rendered above the options every page shares. */
export interface PageActionsMenuItem {
  key: string;
  label: string;
  onClick(): void | Promise<void>;
  isDisabled?: boolean;
  /** Rules a line off above this option, for a page whose options fall into groups that act
   * on different things - the whole view against whichever rows are marked, say. */
  hasDividerBefore?: boolean;
}

export interface PageActionsMenu {
  /** Reloads this page's data, bypassing the store cache */
  onRefresh(): void | Promise<void>;
  /** Removes the record on screen; the confirmation modal is handled here.
   * Omit on pages that have nothing to remove - the option is then hidden. */
  onRemove?(): void | Promise<void>;
  /** Keeps Remove visible but inactive, for entities whose removal is not released yet */
  isRemoveDisabled?: boolean;
  /** confirmation message shown before removing */
  removeConfirmationMessage?: string;
  /** Page specific options, rendered above the shared ones */
  items?: PageActionsMenuItem[];
  /** Replaces the kebab on pages that use a different toggle */
  toggleIcon?: React.ReactNode;
  /** aria-label of the toggle */
  ariaLabel?: string;
}

export const PageActionsMenu: React.FunctionComponent<PageActionsMenu> = ({
  items = [],
  isRemoveDisabled = false,
  removeConfirmationMessage,
  toggleIcon = <EllipsisVIcon />,
  ariaLabel = "Page actions kebab",
  ...props
}) => {
  const { addNotification } = useNotification();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = React.useState(false);

  const onRefreshClick = async () => {
    setIsOpen(false);
    beginLoading();
    try {
      await props.onRefresh();
    } catch (error: any) {
      addNotification('Error refreshing data', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  const onRemoveClick = () => {
    setIsOpen(false);
    setIsRemoveModalOpen(true);
  };

  const onRemoveConfirm = async () => {
    setIsRemoveModalOpen(false);
    await props.onRemove?.();
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={isOpen}
        onSelect={() => setIsOpen(false)}
        onOpenChange={setIsOpen}
        popperProps={{ position: 'right' }}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsOpen(!isOpen)} variant="plain"
            isExpanded={isOpen} aria-label={ariaLabel}>
            {toggleIcon}
          </MenuToggle>
        )}
      >
        <DropdownList>
          {items.map(item => (
            <React.Fragment key={item.key}>
              {item.hasDividerBefore && <Divider component="li" />}
              <DropdownItem isDisabled={item.isDisabled} onClick={() => item.onClick()}>
                {item.label}
              </DropdownItem>
            </React.Fragment>
          ))}
          {items.length > 0 && <Divider component="li" key="page-items-separator" />}
          <DropdownItem key="refresh" onClick={onRefreshClick}>Refresh...</DropdownItem>
          {props.onRemove && (
            <DropdownItem key="remove" isDisabled={isRemoveDisabled} onClick={onRemoveClick}>Remove</DropdownItem>
          )}
        </DropdownList>
      </Dropdown>
      {props.onRemove && (
        <RemoveConfirmModal isOpen={isRemoveModalOpen}
          confirmationMessage={removeConfirmationMessage}
          onRemoveConfirm={onRemoveConfirm}
          onRemoveCancel={() => setIsRemoveModalOpen(false)} />
      )}
    </React.Fragment>
  );
};
